import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import React, { useRef } from "react";
import styled from "styled-components";

import { useBulkAnalyzeClaimsMutation } from "../../store/queries/claims";
import { IClaim } from "../../types/claim";

interface IBulkRunAiAnalysisModalProps {
  visible: boolean;
  onHide: () => void;
  selectedClaims: IClaim[];
  onSuccess: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 28rem;
`;

const Hint = styled.div`
  color: #6c757d;
  font-size: 0.875rem;
`;

/**
 * CLE-2551: confirm-and-start modal for running AI analysis on the selected
 * claims. Before this, analysis could only be triggered one claim at a time
 * from the claim modal's AI Insights tab.
 *
 * Unlike the other bulk modals this doesn't wait for the work: the endpoint
 * returns 202 and the analyses run in the background (a single one is a
 * ~10-40s OpenAI call), so we confirm the kick-off and let the results appear
 * on the claims as each finishes.
 */
export function BulkRunAiAnalysisModal({
  visible,
  onHide,
  selectedClaims,
  onSuccess,
}: IBulkRunAiAnalysisModalProps) {
  const toastRef = useRef<Toast>(null);
  const [bulkAnalyze, { isLoading: isStarting }] =
    useBulkAnalyzeClaimsMutation();

  const handleApply = async () => {
    try {
      const result = await bulkAnalyze({
        claimIds: selectedClaims.map((c) => c.id),
      }).unwrap();

      toastRef.current?.show({
        severity: "success",
        summary: "AI analysis started",
        detail: `Analyzing ${result.claimCount} claim${
          result.claimCount === 1 ? "" : "s"
        } in the background. Results appear on each claim as it finishes.`,
        life: 8000,
      });

      onSuccess();
    } catch (err: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Could not start AI analysis",
        detail:
          err?.data?.error || err?.data?.messages?.[0] || "Request failed.",
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isStarting}
      />
      <Button
        label={`Run on ${selectedClaims.length}`}
        icon="pi pi-sparkles"
        onClick={handleApply}
        disabled={isStarting}
        loading={isStarting}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header="Run AI analysis"
        visible={visible}
        onHide={isStarting ? () => {} : onHide}
        closable={!isStarting}
        footer={footer}
      >
        <Body>
          <div>
            Run AI analysis on <strong>{selectedClaims.length}</strong> selected
            claim
            {selectedClaims.length === 1 ? "" : "s"}. Any existing analysis on
            these claims is replaced.
          </div>

          <Hint>
            This runs in the background and can take a few minutes for a full
            batch — you can keep working. Refresh the list to see results as
            they land.
          </Hint>
        </Body>
      </Dialog>
    </>
  );
}
